import { useMemo, useState } from "react";

import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AgChartPanel,
  buildAgOptions,
  useDefaultPalette,
  type ChartDataset,
} from "@/components/cinch/charts";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { DeltaCell } from "@/components/cinch/DeltaCell";
import { EmptyState } from "@/components/cinch/EmptyState";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Sparkline } from "@/components/cinch/Sparkline";
import { StatusBadge } from "@/components/cinch/StatusBadge";

import {
  fmtMoney0,
  fmtPct,
  fmtRate,
  monthLabel,
  useCosTrends,
  type TrendLine,
} from "../api";

/**
 * Cost trends for the items we use to deliver the product: total COGS by month
 * (stacked by product line) and a per-line table with the $/metric trajectory.
 * Row click jumps into that month's Details.
 */
export function SummaryView({ onOpenMonth }: { onOpenMonth: (month: string) => void }) {
  const [months, setMonths] = useState(12);
  const { data, isLoading, error } = useCosTrends(months);
  const { colors, isDark } = useDefaultPalette();

  const latestIdx = useMemo(() => {
    if (!data) return -1;
    for (let i = data.months.length - 1; i >= 0; i--) {
      if (data.grand_totals[i] != null) return i;
    }
    return -1;
  }, [data]);
  const priorIdx = useMemo(() => {
    if (!data || latestIdx < 0) return -1;
    for (let i = latestIdx - 1; i >= 0; i--) {
      if (data.grand_totals[i] != null) return i;
    }
    return -1;
  }, [data, latestIdx]);

  const stackedOptions = useMemo(() => {
    if (!data || latestIdx < 0) return null;
    const withData = data.lines.filter((ln) => ln.points.some((p) => p.total_expense != null));
    const ds: ChartDataset = {
      key: "cos-total-by-line",
      label: "Monthly COGS by product line",
      kind: "timeseries",
      chartTypes: ["stacked-bar"],
      categoryKey: "month",
      rows: data.months.map((m, idx) => ({
        month: monthLabel(m),
        ...Object.fromEntries(
          withData.map((ln) => [ln.product_line_key, ln.points[idx]?.total_expense ?? null]),
        ),
      })),
      series: withData.map((ln) => ({ key: ln.product_line_key, name: ln.title })),
      colors,
      valueFormat: (n) => fmtMoney0(n),
    };
    return buildAgOptions(ds, "stacked-bar", isDark);
  }, [data, latestIdx, colors, isDark]);

  if (error != null) {
    return (
      <EmptyState title="Couldn't load trends">
        {error instanceof Error ? error.message : String(error)}
      </EmptyState>
    );
  }
  if (!isLoading && (latestIdx < 0 || !data)) {
    return (
      <EmptyState title="No months computed yet">
        Upload a monthly cost workbook on the Uploads tab and map its line items — each
        computed month lands here as a trend point.
      </EmptyState>
    );
  }

  const latestTotal = latestIdx >= 0 ? data?.grand_totals[latestIdx] : null;
  const priorTotal = priorIdx >= 0 ? data?.grand_totals[priorIdx] : null;
  const latestMonth = latestIdx >= 0 ? data?.months[latestIdx] : null;
  const latestStatus =
    latestIdx >= 0 ? data?.lines[0]?.points[latestIdx]?.status ?? null : null;

  const columns: DataTableColumn<TrendLine>[] = [
    {
      key: "title",
      header: "Product line",
      render: (ln) => (
        <div>
          <div className="font-medium">{ln.title}</div>
          <div className="text-xs text-muted-foreground">{ln.metric_label}</div>
        </div>
      ),
    },
    {
      key: "trend",
      header: `$/${"metric"} trend`,
      render: (ln) => {
        const pts = ln.points
          .map((p) => p.dollar_per_metric_actual)
          .filter((v): v is number => v != null);
        return pts.length >= 2 ? (
          <Sparkline points={pts} tone="brand" ariaLabel={`${ln.title} $/metric trend`} />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      key: "per_metric",
      header: "$/metric",
      align: "right",
      numeric: true,
      render: (ln) => fmtRate(latestIdx >= 0 ? ln.points[latestIdx]?.dollar_per_metric_actual : null),
    },
    {
      key: "delta",
      header: "MoM Δ",
      align: "right",
      render: (ln) => {
        const now = latestIdx >= 0 ? ln.points[latestIdx]?.dollar_per_metric_actual : null;
        const before = priorIdx >= 0 ? ln.points[priorIdx]?.dollar_per_metric_actual : null;
        return (
          <DeltaCell
            value={now != null && before != null ? now - before : null}
            invert
            threshold={1e-9}
            format={(n) => fmtRate(n)}
          />
        );
      },
    },
    {
      key: "total",
      header: "Month total",
      align: "right",
      numeric: true,
      render: (ln) => fmtMoney0(latestIdx >= 0 ? ln.points[latestIdx]?.total_expense : null),
    },
    {
      key: "utilization",
      header: "Utilization",
      align: "right",
      render: (ln) => fmtPct(latestIdx >= 0 ? ln.points[latestIdx]?.utilization : null),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          label={latestMonth ? `${monthLabel(latestMonth)} COGS` : "Latest month COGS"}
          value={fmtMoney0(latestTotal)}
          sub={
            latestStatus ? (
              <StatusBadge
                value={latestStatus}
                label={latestStatus === "signed_off" ? "Signed off" : "In progress"}
              />
            ) : undefined
          }
        />
        <KpiTile
          label="MoM change"
          value={
            <DeltaCell
              value={latestTotal != null && priorTotal != null ? latestTotal - priorTotal : null}
              invert
              format={(n) => fmtMoney0(n)}
            />
          }
          sub={priorIdx >= 0 && data ? `vs ${monthLabel(data.months[priorIdx])}` : undefined}
        />
        <KpiTile
          label="Months tracked"
          value={data ? String(data.grand_totals.filter((t) => t != null).length) : "—"}
          sub={`of the last ${months}`}
        />
        <KpiTile
          label="Costliest line"
          value={(() => {
            if (!data || latestIdx < 0) return "—";
            let best: { title: string; total: number } | null = null;
            for (const ln of data.lines) {
              const t = ln.points[latestIdx]?.total_expense;
              if (t != null && (best === null || t > best.total)) {
                best = { title: ln.title, total: t };
              }
            }
            return best ? best.title : "—";
          })()}
          sub={(() => {
            if (!data || latestIdx < 0) return undefined;
            const totals = data.lines
              .map((ln) => ln.points[latestIdx]?.total_expense)
              .filter((v): v is number => v != null);
            return totals.length ? fmtMoney0(Math.max(...totals)) : undefined;
          })()}
        />
      </div>

      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Monthly COGS by product line</h2>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="h-8 w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6">Last 6 mo</SelectItem>
              <SelectItem value="12">Last 12 mo</SelectItem>
              <SelectItem value="24">Last 24 mo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {stackedOptions ? (
          <AgChartPanel options={stackedOptions} className="h-72" />
        ) : (
          <div className="h-72 animate-pulse rounded-md bg-muted/50" />
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={data?.lines ?? []}
        rowKey={(ln) => ln.product_line_key}
        loading={isLoading}
        emptyMessage="No product lines yet."
        onRowClick={latestMonth ? () => onOpenMonth(latestMonth) : undefined}
      />
    </div>
  );
}
